'use client';

import { useEffect, useId, useRef, type CSSProperties } from 'react';
import styles from './BatteryRings.module.css';

const DASH_ON = 3;
const DASH_OFF = 16;
const DASH_PERIOD = DASH_ON + DASH_OFF;
const IDLE_LOOP_SEC = 2;
const MAX_LOOP_SEC = 0.045;
const MOVE_THRESHOLD = 920;
const POINTER_GAIN = 0.32;
const POINTER_SPEED_CAP = 4200;
const TAU_ATTACK = 0.07;
const TAU_RELEASE = 0.42;
const IDLE_SPEED = DASH_PERIOD / IDLE_LOOP_SEC;
const MAX_SPEED = DASH_PERIOD / MAX_LOOP_SEC;

const ringStyle = (
  widthPct: number,
  aspect: string,
  topPct: number,
): CSSProperties =>
  ({
    '--ring-w': `${widthPct}%`,
    '--ring-aspect': aspect,
    '--ring-top': `${topPct}%`,
  }) as CSSProperties;

export const BatteryRings = () => {
  const uid = useId().replace(/:/g, '');
  const outerGradId = `battery-ring-outer-${uid}`;
  const innerGradId = `battery-ring-inner-${uid}`;

  const rootRef = useRef<HTMLDivElement | null>(null);
  const outerRef = useRef<SVGEllipseElement | null>(null);
  const innerRef = useRef<SVGEllipseElement | null>(null);

  useEffect(() => {
    const root = rootRef.current;
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!root || !outer || !inner) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let raf = 0;
    let running = true;
    let visible = true;
    let lastTs = 0;
    let speed = IDLE_SPEED;
    let target = IDLE_SPEED;
    let offset = 0;
    let lastX = 0;
    let lastY = 0;
    let lastMoveTs = 0;
    let hovering = false;

    const tick = (ts: number) => {
      if (!running) return;
      raf = 0;
      if (!visible) {
        lastTs = 0;
        return;
      }

      const dt = lastTs ? Math.min(0.05, (ts - lastTs) / 1000) : 0;
      lastTs = ts;

      if (dt > 0) {
        if (!hovering || ts - lastMoveTs > 90) target = IDLE_SPEED;
        const tau = speed < target ? TAU_ATTACK : TAU_RELEASE;
        speed += (target - speed) * (1 - Math.exp(-dt / tau));
        offset = (offset + speed * dt) % DASH_PERIOD;
        outer.setAttribute('stroke-dashoffset', String(offset));
        inner.setAttribute('stroke-dashoffset', String(-offset));
      }

      raf = requestAnimationFrame(tick);
    };

    const ensureTick = () => {
      if (!running || !visible || raf) return;
      lastTs = 0;
      raf = requestAnimationFrame(tick);
    };

    const onPointerMove = (event: PointerEvent) => {
      const now = performance.now();
      if (hovering && lastMoveTs) {
        const moveDt = Math.max(0.008, (now - lastMoveTs) / 1000);
        const dist = Math.hypot(event.clientX - lastX, event.clientY - lastY);
        const pointerSpeed = Math.min(POINTER_SPEED_CAP, dist / moveDt);
        target =
          pointerSpeed >= MOVE_THRESHOLD
            ? Math.min(
                MAX_SPEED,
                IDLE_SPEED + POINTER_GAIN * (pointerSpeed - MOVE_THRESHOLD),
              )
            : IDLE_SPEED;
      }
      hovering = true;
      lastX = event.clientX;
      lastY = event.clientY;
      lastMoveTs = now;
    };

    const onPointerLeave = () => {
      hovering = false;
      lastMoveTs = 0;
      target = IDLE_SPEED;
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        visible = Boolean(entry?.isIntersecting);
        if (visible) ensureTick();
      },
      { threshold: 0.05 },
    );
    io.observe(root);

    root.addEventListener('pointermove', onPointerMove);
    root.addEventListener('pointerleave', onPointerLeave);
    root.addEventListener('pointercancel', onPointerLeave);
    ensureTick();

    return () => {
      running = false;
      io.disconnect();
      root.removeEventListener('pointermove', onPointerMove);
      root.removeEventListener('pointerleave', onPointerLeave);
      root.removeEventListener('pointercancel', onPointerLeave);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div ref={rootRef} className={styles.root} aria-hidden="true">
      <svg
        className={styles.ring}
        style={ringStyle(90.449, '644 / 249', 72.78)}
        viewBox="0 0 644 249"
        fill="none"
      >
        <defs>
          <linearGradient
            id={outerGradId}
            x1="322"
            y1="4"
            x2="322"
            y2="245"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#75E65E" stopOpacity="0" />
            <stop offset="1" stopColor="#75E65E" />
          </linearGradient>
        </defs>
        <ellipse
          ref={outerRef}
          cx="322"
          cy="124.5"
          rx="318"
          ry="120.5"
          stroke={`url(#${outerGradId})`}
          strokeWidth="8"
          strokeMiterlimit="3.4203"
          strokeDasharray={`${DASH_ON} ${DASH_OFF}`}
        />
      </svg>
      <svg
        className={styles.ring}
        style={ringStyle(62.921, '448 / 169', 67.59)}
        viewBox="0 0 448 169"
        fill="none"
      >
        <defs>
          <linearGradient
            id={innerGradId}
            x1="224"
            y1="4"
            x2="224"
            y2="165"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#75E65E" stopOpacity="0" />
            <stop offset="1" stopColor="#75E65E" />
          </linearGradient>
        </defs>
        <ellipse
          ref={innerRef}
          cx="224"
          cy="84.5"
          rx="220"
          ry="80.5"
          stroke={`url(#${innerGradId})`}
          strokeWidth="8"
          strokeMiterlimit="3.4203"
          strokeDasharray={`${DASH_ON} ${DASH_OFF}`}
        />
      </svg>
    </div>
  );
};
